/** The admin pages: a side menu of six pages, each about one thing. */
import { FolderOpen, HardDrive, LayoutDashboard, ScrollText, Server, Users } from "lucide-react";
import { useEffect, type ReactNode } from "react";
import { Link, useSession } from "../lib/app";
import { TopNav } from "../pages/Dashboard";
import { AuditLogPage } from "./AuditLog";
import { fullnessTone, syncProblem, URLS, useResource, type StorageReport, type SyncResponse, type UsersResponse } from "./data";
import { HostingPage } from "./Hosting";
import { OverviewPage } from "./Overview";
import { PeoplePage } from "./People";
import { ProjectsPage } from "./Projects";
import { StoragePage } from "./Storage";
import { AdminLink, type Tone } from "./ui";
import "./admin.css";

const PAGES: Array<{ path: string; label: string; title: string; icon: typeof Users; page: () => ReactNode; wide?: boolean }> = [
  { path: "/admin", label: "Overview", title: "Admin", icon: LayoutDashboard, page: OverviewPage },
  { path: "/admin/people", label: "People", title: "People", icon: Users, page: PeoplePage },
  { path: "/admin/projects", label: "Projects", title: "Projects", icon: FolderOpen, page: ProjectsPage },
  { path: "/admin/storage", label: "Storage", title: "Storage", icon: HardDrive, page: StoragePage },
  { path: "/admin/hosting", label: "Hosting", title: "Hosting", icon: Server, page: HostingPage },
  { path: "/admin/audit", label: "Audit log", title: "Audit log", icon: ScrollText, page: AuditLogPage, wide: true },
];

export function AdminArea({ path }: { path: string }) {
  const { user } = useSession();
  const current = PAGES.find((p) => p.path === path) ?? PAGES[0];
  useEffect(() => {
    document.title = `${current.title} · benleaf`;
    return () => {
      document.title = "benleaf";
    };
  }, [current.title]);
  // A new page starts at the top, like a real page load.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [current.path]);

  if (!user?.isAdmin) {
    return (
      <div className="page">
        <TopNav />
        <main className="page-main">
          <div className="empty-state">
            <h3>Admins only</h3>
            <p>
              Your account can't open the admin pages. <Link to="/">Back to your projects</Link>
            </p>
          </div>
        </main>
      </div>
    );
  }

  const Page = current.page;
  return (
    <div className="page">
      <TopNav />
      <div className="admin">
        <AdminNav path={current.path} />
        <main className={`admin-main${current.wide ? " wide" : ""}`} key={current.path}>
          <Page />
        </main>
      </div>
    </div>
  );
}

/** The side menu. Badges point at what needs doing: people to verify, sync failures, a filling disk. */
function AdminNav({ path }: { path: string }) {
  const { canManageSync } = useSession();
  const users = useResource<UsersResponse>(URLS.users);
  const storage = useResource<StorageReport>(URLS.storage);
  const sync = useResource<SyncResponse>(canManageSync ? URLS.sync : null);

  const unverified = users.data?.users.filter((u) => !u.verified && !u.isAdmin).length ?? 0;
  const failing = sync.data?.projects.filter(syncProblem).length ?? 0;
  const disk = storage.data?.disk;
  const fullness = disk?.total ? disk.used / disk.total : 0;

  const badges: Record<string, { text: string; tone: Tone; title: string } | null> = {
    "/admin/people": unverified ? { text: String(unverified), tone: "warn", title: `${unverified} waiting to be verified` } : null,
    "/admin/projects": failing ? { text: String(failing), tone: "bad", title: `Sync is failing for ${failing}` } : null,
    "/admin/storage":
      fullness >= 0.8 ? { text: `${Math.round(fullness * 100)}%`, tone: fullnessTone(fullness) as Tone, title: "The disk is getting full" } : null,
  };

  return (
    <nav className="admin-nav" aria-label="Admin pages">
      <div className="admin-nav-label">Admin</div>
      {PAGES.map((p) => {
        const badge = badges[p.path];
        const Icon = p.icon;
        return (
          <AdminLink key={p.path} to={p.path} current={p.path === path} className={`admin-nav-item${p.path === path ? " on" : ""}`}>
            <Icon size={16} strokeWidth={1.9} aria-hidden="true" />
            <span className="grow">{p.label}</span>
            {badge && (
              <>
                <span className={`admin-nav-badge tone-${badge.tone}`} title={badge.title} aria-hidden="true">
                  {badge.text}
                </span>
                <span className="sr-only">: {badge.title}</span>
              </>
            )}
          </AdminLink>
        );
      })}
    </nav>
  );
}
